import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { onValue, ref, set } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { zonesForHole, ZONES } from './prodMapPoints.js'
import { computeMatchDots } from './matchTracking.js'
import { TV_BG_GREEN } from '../config/tvTheme.js'
import TvStage from './TvStage.jsx'
import TvScroll from './TvScroll.jsx'

// ── PRODUCTION Course Scroll Map ──────────────────────────────────────────
// The real-tournament version of the scrolling course map: cream/crimson hole
// tiles + full course image + geofence tee/fairway/green dots, with a PLACE
// SPOTS tool for positioning the staged dots on every hole. It lives in the TV
// Monitor section and is SEPARATE from the /teststation/map2 home sandbox:
//   • REAL coords (prodMapPoints.js); radius 27 m (Hole 1 is on a 25-yd home-test override).
//   • Placed spots persist to /config/productionMap/spots (NOT the sandbox path).
//   • The live geofence dot follows the SCORECARD's own GPS: it reads the freshest
//     tournaments/{tid}/tracking/{teamId} write across open tournaments — the same
//     node the qualifying/match scorecard reports to — NOT the calibrator broadcaster.
// Nothing here reads or writes the sandbox's coords or spots.

const MAP_IMAGE = '/pbccmap77.png?v=1'
// Board background behind the course map — the shared dark TV green so the
// Course Scroll Map matches every other TV board.
const MAP_FIELD = TV_BG_GREEN

const IMG_W = 3876
const IMG_H = 2696
const VIS_W = IMG_W
const MAP_AR = VIS_W / IMG_H
const VIS_SCALE = IMG_W / VIS_W // = 1

const STAGE_W = 1920
const STAGE_H = 1080
const MAP_W = Math.round(STAGE_H * MAP_AR)
const COL_W = STAGE_W - MAP_W
const COL_FONT = 17
// SEPARATE from the sandbox's /config/calibrationMap2/spots — placing here can
// never change the home test.
const SPOTS_PATH = 'config/productionMap/spots'
// Live position is stale if the newest tracking write is older than this. The
// scorecard reports at most every ~8s (throttled), so 20s keeps the dot steady
// between fixes instead of blinking off.
const STALE_MS = 20000

const HOLES = Array.from({ length: 18 }, (_, i) => i + 1)
const round2 = (n) => Math.round(n * 100) / 100

const ZONE_ASSET = {
  tee:     { img: '/teebox1.png?v=2',  label: 'TB' },
  fairway: { img: '/fairway1.png?v=1', label: 'FW' },
  green:   { img: '/green1.png?v=1',   label: 'GR' },
}

// `scroll` = true → side hole-tile strip + map (the TV Monitor view). false →
// the course image + dots fill the whole screen, no strip (used everywhere the
// old Command Center course map used to show). Both read the same real coords,
// the same geofence dots, and the same placed spots (config/productionMap/spots).
export default function TvCourseScrollMap({ tournamentIdProp = null, embedded = false, scroll = true } = {}) {
  const [snap, setSnap] = useState(null)   // raw tournaments tree
  const [dots, setDots] = useState([])     // one dot per active match
  const [now, setNow] = useState(() => Date.now())
  const [spots, setSpots] = useState({})
  const [placeMode, setPlaceMode] = useState(false)
  const [hole, setHole] = useState(1)
  const [zone, setZone] = useState('tee')
  const [status, setStatus] = useState('')
  const [scrollSpeed, setScrollSpeed] = useState('slow')
  // Persistent match state across renders (forward-only zones, green ownership,
  // per-match freshness on OUR clock) — mutated in place by computeMatchDots.
  const matchStateRef = useRef({ zone: {}, greenOwner: {}, cleared: {}, fresh: {} })

  // Admin scroll-speed (shared with the leaderboard boards via TvScroll).
  useEffect(() => {
    if (!tournamentIdProp) return undefined
    const off = onValue(
      tref(`tournaments/${tournamentIdProp}/tvSettings/scrollSpeed`),
      (snap) => setScrollSpeed(snap.val() || 'slow'),
      () => {},
    )
    return () => { try { off() } catch {} }
  }, [tournamentIdProp])

  // Live positions — read tournaments/*/tracking (the SAME node the qualifying/
  // match scorecard writes). computeMatchDots turns every fix into ONE dot per
  // match (freshest of the match's teams), with forward-only zones + one-green-
  // one-match ownership. NOT the gpstest/calibrator broadcaster.
  useEffect(() => {
    const off = onValue(tref('tournaments'), (s) => setSnap(s.val() || {}), () => setSnap(null))
    return () => { try { off() } catch {} }
  }, [])

  // Recompute the match dots on each tracking update AND each staleness tick, so
  // dots also expire when a phone stops reporting.
  useEffect(() => {
    setDots(computeMatchDots(snap || {}, spots, matchStateRef.current, Date.now(), STALE_MS))
  }, [snap, now, spots])

  // Staleness tick.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 3000)
    return () => clearInterval(id)
  }, [])

  // Production staged spots (its OWN isolated path).
  useEffect(() => {
    const off = onValue(
      tref(SPOTS_PATH),
      (snap) => {
        const v = snap.val()
        if (v && typeof v === 'object') setSpots(v)
      },
      () => {},
    )
    return () => { try { off() } catch {} }
  }, [])

  const spotFor = (h, z) => spots?.[h]?.[z] || null

  // Left-strip rows: one per active match on its hole; the rest render as idle tees.
  const byHole = {}
  for (const d of dots) (byHole[d.hole] || (byHole[d.hole] = [])).push({ hole: d.hole, zone: d.zone, teamA: d.names[0] || null, teamB: d.names[1] || null })
  const displayMatches = []
  for (let h = 1; h <= 18; h++) {
    if (byHole[h]) displayMatches.push(...byHole[h])
    else displayMatches.push({ hole: h, zone: 'tee' })
  }

  function placeAt(e) {
    if (!placeMode) return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    if (!rect.width || !rect.height) return
    const clamp = (n) => Math.max(0, Math.min(100, n))
    const x = round2(clamp((((e.clientX - rect.left) / rect.width) * 100) / VIS_SCALE))
    const y = round2(clamp(((e.clientY - rect.top) / rect.height) * 100))
    if (!Number.isFinite(x) || !Number.isFinite(y)) return
    setSpots((prev) => ({ ...prev, [hole]: { ...(prev[hole] || {}), [zone]: { x, y } } }))
    setStatus(`H${hole} ${zone} → ${x}, ${y} (unsaved)`)
  }

  async function saveSpots() {
    try { await set(tref(SPOTS_PATH), spots); setStatus('saved ✓ (synced to all devices)') }
    catch (err) { setStatus('save failed: ' + (err?.message || String(err))) }
  }

  async function exportSpots() {
    const json = JSON.stringify(spots, null, 2)
    try { await navigator.clipboard.writeText(json); setStatus('copied JSON to clipboard ✓') }
    catch { setStatus('copy failed — select the JSON below manually') }
  }

  const placed = []
  for (const h of Object.keys(spots || {})) {
    for (const z of Object.keys(spots[h] || {})) {
      const p = spots[h][z]
      if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) placed.push({ hole: h, zone: z, x: p.x, y: p.y })
    }
  }
  placed.sort((a, b) => Number(a.hole) - Number(b.hole) || ZONES.indexOf(a.zone) - ZONES.indexOf(b.zone))

  // Zones available for the picked hole (par 3s: tee + green only).
  const holeZones = zonesForHole(hole)

  return (
    <TvStage embedded={embedded} designW={STAGE_W} designH={STAGE_H} background={MAP_FIELD}>
      {/* Controls tucked into the FAR-RIGHT edge (Home + Place spots), out of the
          way of the map. Stacked in the top-right corner. */}
      <div style={{ position: 'absolute', top: 6, right: 8, zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
        {!embedded ? <Link to="/" style={NAV}>🏠 Home</Link> : null}
        <button
          onClick={() => setPlaceMode((v) => !v)}
          style={{ ...NAV, cursor: 'pointer', background: placeMode ? '#7C2D12' : 'rgba(0,0,0,0.55)', borderColor: placeMode ? '#F59E0B' : `${CLUB.accentColor || '#FFFFFF'}88` }}
        >
          {placeMode ? '✓ Placing…' : '✎ Place spots'}
        </button>
      </div>

      {/* Course map — right-aligned, full stage height. */}
      <div
        onPointerDown={placeAt}
        style={{
          // Scroll view: map right-aligned at MAP_W (strip fills the rest). No-
          // scroll view: map fills the whole stage.
          position: 'absolute', top: 0, right: 0, width: scroll ? MAP_W : STAGE_W, height: STAGE_H, overflow: 'hidden',
          cursor: placeMode ? 'crosshair' : 'default',
          touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none',
        }}
      >
        <img
          src={MAP_IMAGE}
          alt="PBCC Course Map"
          draggable={false}
          style={{ position: 'absolute', left: 0, top: 0, width: `${VIS_SCALE * 100}%`, height: '100%', objectFit: 'fill', display: 'block', pointerEvents: 'none' }}
        />

        {/* One dot per active match — geofence-driven, one-green-one-match. */}
        {dots.map((d) => (
          <div key={d.key} style={{ ...MATCH_DOT, left: `${d.x * VIS_SCALE}%`, top: `${d.y}%` }} />
        ))}

        {/* Placement preview markers for the selected hole (par-3 aware). */}
        {placeMode && holeZones.map((z) => {
          const p = spotFor(hole, z)
          if (!p) return null
          const active = z === zone
          return (
            <div key={z} style={{
              position: 'absolute', left: `${p.x * VIS_SCALE}%`, top: `${p.y}%`, transform: 'translate(-50%, -50%)',
              zIndex: 30, display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none',
            }}>
              <div style={{
                width: active ? 20 : 14, height: active ? 20 : 14, borderRadius: '50%',
                background: active ? '#F59E0B' : 'rgba(245,158,11,0.35)',
                border: `2px solid ${active ? '#fff' : '#F59E0B'}`, boxShadow: '0 1px 6px rgba(0,0,0,0.6)',
              }} />
              <span style={{
                marginTop: 2, fontSize: 10, fontWeight: 800, color: '#fff',
                background: 'rgba(0,0,0,0.6)', padding: '1px 5px', borderRadius: 6, whiteSpace: 'nowrap',
              }}>{z}</span>
            </div>
          )
        })}
      </div>

      {/* Left scrolling hole column — SCROLL mode only (TV Monitor). Uses the
          ONE shared TvScroll (constant px/sec, admin scroll-speed) so it moves
          in lockstep with the leaderboard boards and can't drift apart. */}
      {scroll ? (
        <div style={{ ...LEFT_COL, width: COL_W, fontSize: COL_FONT }}>
          <TvScroll speed={scrollSpeed} innerClassName="">
            {displayMatches.map((m, i) => (
              <div key={i} style={HOLE_BOX}>
                <MatchBox hole={m.hole} zone={m.zone} teamA={m.teamA} teamB={m.teamB} />
              </div>
            ))}
          </TvScroll>
          <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
        </div>
      ) : null}

      {/* PBCC logo bottom-right — hidden while placing so the (bottom-right)
          Place Spots panel isn't blocked. Normal TV view is unchanged. */}
      {!placeMode ? (
        <img
          src={CLUB.logoLight}
          alt="PBCC"
          draggable={false}
          style={{
            position: 'absolute', right: 110, bottom: 50,
            width: 305, height: 'auto', zIndex: 46, opacity: 0.95, pointerEvents: 'none',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.55))',
          }}
        />
      ) : null}

      {placeMode ? (
        <div style={PANEL}>
          <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: '0.05em', textTransform: 'uppercase', color: CLUB.accentColor || '#FFFFFF' }}>
            Place staged spots
          </div>
          <div style={{ fontSize: 11, opacity: 0.85, lineHeight: 1.35 }}>
            Pick a hole + zone, then click / tap the map to drop that dot. Re-tap to move it. Par 3s have tee + green only.
          </div>

          <label style={ROW}>
            <span style={LBL}>Hole</span>
            <select
              value={hole}
              onChange={(e) => {
                const h = Number(e.target.value)
                setHole(h)
                // Par-3 holes have no fairway zone — snap the picker back to tee.
                if (!zonesForHole(h).includes(zone)) setZone('tee')
              }}
              style={SELECT}>
              {HOLES.map((h) => <option key={h} value={h}>Hole {h}{zonesForHole(h).length === 2 ? ' (par 3)' : ''}</option>)}
            </select>
          </label>

          <div style={ROW}>
            <span style={LBL}>Zone</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {holeZones.map((z) => (
                <button key={z} onClick={() => setZone(z)} style={{ ...CHIP, ...(z === zone ? CHIP_ON : null) }}>{z}</button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={saveSpots} style={{ ...BTN, background: '#166534', borderColor: '#22C55E' }}>💾 Save</button>
            <button onClick={exportSpots} style={BTN}>⧉ Export JSON</button>
          </div>

          {status ? <div style={{ fontSize: 11, color: '#FCD34D', minHeight: 14 }}>{status}</div> : null}

          <div style={{ fontSize: 11, fontWeight: 800, opacity: 0.85, marginTop: 2 }}>
            Placed spots ({placed.length})
          </div>
          <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            {placed.length === 0 ? (
              <div style={{ fontSize: 11, opacity: 0.6 }}>none yet</div>
            ) : placed.map((s) => (
              <button
                key={`${s.hole}-${s.zone}`}
                onClick={() => { setHole(Number(s.hole)); setZone(s.zone) }}
                style={{
                  ...LIST_ROW,
                  ...(Number(s.hole) === hole && s.zone === zone ? { background: 'rgba(245,158,11,0.25)', borderColor: '#F59E0B' } : null),
                }}
              >
                <span style={{ fontWeight: 800 }}>H{s.hole}</span>
                <span style={{ opacity: 0.85 }}>{s.zone}</span>
                <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{s.x}, {s.y}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </TvStage>
  )
}

const NAV = {
  color: CLUB.accentColor || '#FFFFFF', background: 'rgba(0,0,0,0.55)',
  border: `1px solid ${CLUB.accentColor || '#FFFFFF'}88`, padding: '5px 10px', borderRadius: 8,
  fontSize: 12, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', textDecoration: 'none',
}

function MatchBox({ hole, zone, teamA, teamB }) {
  const circleBase = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, textAlign: 'center',
    width: '1.95em', height: '1.95em', borderRadius: '50%', background: '#950808', color: '#FFFFFF',
    fontFamily: '"Marcellus", Georgia, serif',
    fontVariantNumeric: 'lining-nums tabular-nums', fontFeatureSettings: '"lnum" 1, "tnum" 1',
    fontWeight: 400, textShadow: 'none', WebkitTextStroke: '0.018em #FFFFFF',
    boxShadow: '0 0.06em 0.25em rgba(0,0,0,0.45)', flexShrink: 0,
  }
  const nameStyle = { fontWeight: 700, color: '#000000', whiteSpace: 'nowrap', fontSize: '1.35em' }
  const asset = ZONE_ASSET[zone] || ZONE_ASSET.tee
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      {teamA ? <span style={{ ...nameStyle, marginBottom: '0.18em' }}>{teamA}</span> : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45em', fontSize: '1.25em' }}>
        <span style={{ ...circleBase, fontSize: '1em' }}>{hole}</span>
        <img src={asset.img} alt={zone} draggable={false} style={{ display: 'block', width: '4.5em', height: 'auto' }} />
        <span style={{ ...circleBase, fontSize: '1em' }}>{asset.label}</span>
      </div>
      {teamB ? <span style={{ ...nameStyle, marginTop: '0.18em' }}>{teamB}</span> : null}
    </div>
  )
}

const LEFT_COL = {
  position: 'absolute', top: 0, bottom: 0, left: 0, zIndex: 30,
  background: '#04180A', overflow: 'hidden',
  color: '#fff', fontFamily: '"Marcellus", Georgia, serif', lineHeight: 1.3,
}
const MATCH_DOT = {
  position: 'absolute', transform: 'translate(-50%, -50%)', width: 22, height: 22, borderRadius: '50%',
  background: '#FF1A1A', border: '3px solid #fff', boxShadow: '0 2px 10px rgba(0,0,0,0.6)', zIndex: 20,
}
const HOLE_BOX = {
  background: '#FFFFFF', border: '0.08em solid rgba(149,8,8,0.5)',
  borderRadius: '0.8em', padding: '0.65em 1.1em', margin: '0 0.95em 0.65em',
  boxShadow: '0 0.1em 0.3em rgba(0,0,0,0.35)',
}
const PANEL = {
  // Bottom-right so it stays off the right-side holes (18, 9, 10). The PBCC logo
  // that normally sits here is hidden while placing (see the logo render).
  position: 'absolute', bottom: 8, right: 8, zIndex: 50, width: 220,
  maxHeight: 'calc(100% - 16px)', overflowY: 'auto',
  display: 'flex', flexDirection: 'column', gap: 8,
  background: 'rgba(10,20,15,0.92)', color: '#fff',
  border: `1px solid ${CLUB.accentColor || '#FFFFFF'}66`, borderRadius: 12, padding: 12,
  boxShadow: '0 6px 24px rgba(0,0,0,0.5)', touchAction: 'auto',
}
const ROW = { display: 'flex', alignItems: 'center', gap: 8 }
const LBL = { fontSize: 11, fontWeight: 800, width: 38, opacity: 0.85 }
const SELECT = { flex: 1, padding: '4px 6px', borderRadius: 6, border: '1px solid #ffffff33', background: '#0A1810', color: '#fff', fontSize: 12 }
const CHIP = { padding: '4px 8px', borderRadius: 6, border: '1px solid #ffffff33', background: 'transparent', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }
const CHIP_ON = { background: '#F59E0B', borderColor: '#fff', color: '#1a1a1a' }
const BTN = { flex: 1, padding: '6px 8px', borderRadius: 8, border: `1px solid ${CLUB.accentColor || '#FFFFFF'}88`, background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: 12, fontWeight: 800, cursor: 'pointer' }
const LIST_ROW = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '3px 6px', borderRadius: 6, border: '1px solid #ffffff22', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: 11, cursor: 'pointer' }
